import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Tooltip,
  Avatar,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  InputAdornment,
  LinearProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
  Fade,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  LocalOffer as PriceIcon,
  Restaurant as RestaurantIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useSocket } from "../../context/SocketContext";
import {
  fetchMenu,
  addMenuItem,
  updateMenuItem,
  deleteMenuItem,
  toggleMenuItem,
  uploadMenuExcel,
  clearError,
  clearSuccess,
} from "../../redux/slices/menuSlice";
import { getCategories } from "../../redux/slices/categorySlice";
import * as XLSX from "xlsx";
import {
  selectCategories,
} from "../../redux/slices/categorySlice";

const MenuManagement = () => {
  const dispatch = useDispatch();
  const { socket, isConnected } = useSocket();
  
  // Redux state
  const categories = useSelector(selectCategories);
  const { list, loading, error, success, message } = useSelector(
    (state) => state.menu,
  );

  // Local state
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedItem, setSelectedItem] = useState(null);
  const [excelFile, setExcelFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [notification, setNotification] = useState(null);
  
  // Image State
  const [menuImage, setMenuImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // ============ FORM STATE ============
  const [formData, setFormData] = useState({
    itemName: "",
    description: "",
    basePrice: "",
    categoryId: "",
    isAvailable: true,
    isVeg: true,
    sizes: [],
    variants: [],
    addonGroups: [],
  });

  // Temporary states for dynamic fields
  const [currentSize, setCurrentSize] = useState({ name: "", price: "" });
  const [currentVariant, setCurrentVariant] = useState({ name: "", price: "" });
  const [currentAddonGroup, setCurrentAddonGroup] = useState({
    title: "",
    required: false,
    multiSelect: true,
    items: [],
  });
  const [currentAddonItem, setCurrentAddonItem] = useState({ name: "", price: "" });
  const [selectedAddonGroupIndex, setSelectedAddonGroupIndex] = useState(null);

  // ============================================
  // ✅ SOCKET EVENT LISTENERS
  // ============================================
  useEffect(() => {
    if (!socket) return;

    // console.log("📡 Menu socket listeners setup");

    // ============================================
    // 1️⃣ MENU ITEM ADDED
    // ============================================
    socket.on('menu-item-added', (data) => {
      // console.log('🆕 New menu item added in real-time:', data);
      
      // Refresh menu list
      dispatch(fetchMenu());
      
      setNotification({
        message: `🆕 New item: ${data.item.itemName} added!`,
        severity: "success",
      });
      setTimeout(() => setNotification(null), 5000);
    });

    // ============================================
    // 2️⃣ MENU ITEM UPDATED
    // ============================================
    socket.on('menu-updated', (data) => {
      // console.log('📝 Menu item updated in real-time:', data);
      
      // Refresh menu list
      dispatch(fetchMenu());
      
      setNotification({
        message: `📝 ${data.item.itemName} updated!`,
        severity: "info",
      });
      setTimeout(() => setNotification(null), 5000);
    });

    // ============================================
    // 3️⃣ MENU ITEM DELETED
    // ============================================
    socket.on('menu-item-deleted', (data) => {
      // console.log('❌ Menu item deleted in real-time:', data);
      
      // Refresh menu list
      dispatch(fetchMenu());
      
      setNotification({
        message: `❌ ${data.itemName} removed from menu`,
        severity: "warning",
      });
      setTimeout(() => setNotification(null), 5000);
    });

    // ============================================
    // 4️⃣ STOCK UPDATED
    // ============================================
    socket.on('menu-stock-updated', (data) => {
      // console.log('📦 Stock updated in real-time:', data);
      
      // Refresh menu list
      dispatch(fetchMenu());
      
      setNotification({
        message: data.isAvailable 
          ? `✅ ${data.itemName} is back in stock!` 
          : `❌ ${data.itemName} is out of stock!`,
        severity: data.isAvailable ? "success" : "error",
      });
      setTimeout(() => setNotification(null), 5000);
    });

    // ============================================
    // 5️⃣ NOTIFICATION EVENT
    // ============================================
    socket.on('new-notification', (data) => {
      // console.log('🔔 New notification:', data);
      setNotification({
        message: data.notification?.message || 'New notification',
        severity: "info",
      });
      setTimeout(() => setNotification(null), 5000);
    });

    return () => {
      socket.off('menu-item-added');
      socket.off('menu-updated');
      socket.off('menu-item-deleted');
      socket.off('menu-stock-updated');
      socket.off('new-notification');
    };
  }, [socket, dispatch]);

  // ============================================
  // ✅ NOTIFICATION SOUND
  // ============================================
  const playNotificationSound = () => {
    try {
      const audio = new Audio('/sounds/notification.mp3');
      audio.volume = 0.3;
      audio.play().catch(e => console.log('Audio play failed'));
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  useEffect(() => {
    if (notification && notification.severity === 'success') {
      playNotificationSound();
    }
  }, [notification]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMenuImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  // Fetch data on component mount
  useEffect(() => {
    dispatch(fetchMenu());
    dispatch(getCategories());
  }, [dispatch]);

  // Handle success/error messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        dispatch(clearSuccess());
      }, 3000);
      return () => clearTimeout(timer);
    }
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error, dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // ============ SIZES HANDLERS ============
  const handleAddSize = () => {
    if (currentSize.name && currentSize.price) {
      setFormData({
        ...formData,
        sizes: [...formData.sizes, { ...currentSize, price: Number(currentSize.price) }],
      });
      setCurrentSize({ name: "", price: "" });
    }
  };

  const handleRemoveSize = (index) => {
    const updatedSizes = formData.sizes.filter((_, i) => i !== index);
    setFormData({ ...formData, sizes: updatedSizes });
  };

  // ============ VARIANTS HANDLERS ============
  const handleAddVariant = () => {
    if (currentVariant.name) {
      setFormData({
        ...formData,
        variants: [
          ...formData.variants,
          { 
            ...currentVariant, 
            price: currentVariant.price ? Number(currentVariant.price) : 0 
          },
        ],
      });
      setCurrentVariant({ name: "", price: "" });
    }
  };

  const handleRemoveVariant = (index) => {
    const updatedVariants = formData.variants.filter((_, i) => i !== index);
    setFormData({ ...formData, variants: updatedVariants });
  };

  // ============ ADDON GROUPS HANDLERS ============
  const handleAddAddonItem = () => {
    if (currentAddonItem.name && currentAddonItem.price) {
      setCurrentAddonGroup({
        ...currentAddonGroup,
        items: [
          ...currentAddonGroup.items,
          { ...currentAddonItem, price: Number(currentAddonItem.price) },
        ],
      });
      setCurrentAddonItem({ name: "", price: "" });
    }
  };

  const handleRemoveAddonItem = (index) => {
    const updatedItems = currentAddonGroup.items.filter((_, i) => i !== index);
    setCurrentAddonGroup({ ...currentAddonGroup, items: updatedItems });
  };

  const handleAddAddonGroup = () => {
    if (currentAddonGroup.title && currentAddonGroup.items.length > 0) {
      setFormData({
        ...formData,
        addonGroups: [...formData.addonGroups, currentAddonGroup],
      });
      setCurrentAddonGroup({
        title: "",
        required: false,
        multiSelect: true,
        items: [],
      });
      setSelectedAddonGroupIndex(null);
    }
  };

  const handleEditAddonGroup = (index) => {
    setSelectedAddonGroupIndex(index);
    setCurrentAddonGroup(formData.addonGroups[index]);
  };

  const handleUpdateAddonGroup = () => {
    if (selectedAddonGroupIndex !== null) {
      const updatedGroups = [...formData.addonGroups];
      updatedGroups[selectedAddonGroupIndex] = currentAddonGroup;
      setFormData({ ...formData, addonGroups: updatedGroups });
      setSelectedAddonGroupIndex(null);
      setCurrentAddonGroup({
        title: "",
        required: false,
        multiSelect: true,
        items: [],
      });
    }
  };

  const handleRemoveAddonGroup = (index) => {
    const updatedGroups = formData.addonGroups.filter((_, i) => i !== index);
    setFormData({ ...formData, addonGroups: updatedGroups });
  };

  // ============ FORM SUBMISSION ============
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.itemName || !formData.basePrice || !formData.categoryId) {
      alert("Please fill all required fields");
      return;
    }

    const formDataObj = new FormData();
    
    formDataObj.append("itemName", formData.itemName);
    formDataObj.append("description", formData.description || "");
    formDataObj.append("basePrice", formData.basePrice);
    formDataObj.append("categoryId", formData.categoryId);
    formDataObj.append("isAvailable", formData.isAvailable);
    formDataObj.append("isVeg", formData.isVeg);
    formDataObj.append("sizes", JSON.stringify(formData.sizes));
    formDataObj.append("variants", JSON.stringify(formData.variants));
    formDataObj.append("addonGroups", JSON.stringify(formData.addonGroups));

    if (menuImage) {
      formDataObj.append("image", menuImage);
    }

    if (editMode && currentItem) {
      dispatch(
        updateMenuItem({
          id: currentItem._id,
          data: formDataObj,
        }),
      );
    } else {
      dispatch(addMenuItem(formDataObj));
    }
    
    resetForm();
  };

  const handleEdit = (item) => {
    setFormData({
      itemName: item.itemName || "",
      description: item.description || "",
      basePrice: item.basePrice || item.price || "",
      categoryId: item.category?._id || item.category || "",
      isAvailable: item.isAvailable ?? true,
      isVeg: item.isVeg ?? true,
      sizes: item.sizes || [],
      variants: item.variants || [],
      addonGroups: item.addonGroups || [],
    });

    setImagePreview(item.image || null);
    setMenuImage(null);
    setCurrentItem(item);
    setEditMode(true);
    setOpenDialog(true);
  };

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedItem) {
      dispatch(deleteMenuItem(selectedItem._id));
      setOpenDeleteDialog(false);
      setSelectedItem(null);
    }
  };

  const handleExcelUpload = async () => {
    if (!excelFile) {
      alert("Please select an Excel file");
      return;
    }

    const formDataObj = new FormData();
    formDataObj.append("file", excelFile);

    setUploading(true);
    try {
      await dispatch(uploadMenuExcel(formDataObj)).unwrap();
      setOpenUploadDialog(false);
      setExcelFile(null);
      dispatch(fetchMenu());
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      itemName: "",
      description: "",
      basePrice: "",
      categoryId: "",
      isAvailable: true,
      isVeg: true,
      sizes: [],
      variants: [],
      addonGroups: [],
    });
    setCurrentItem(null);
    setEditMode(false);
    setOpenDialog(false);
    setMenuImage(null);
    setImagePreview(null);
    setCurrentSize({ name: "", price: "" });
    setCurrentVariant({ name: "", price: "" });
    setCurrentAddonGroup({
      title: "",
      required: false,
      multiSelect: true,
      items: [],
    });
    setCurrentAddonItem({ name: "", price: "" });
    setSelectedAddonGroupIndex(null);
  };

  // Filter ONLY - NO SORT
  const filteredItems = list.filter((item) => {
    const matchesSearch = item?.itemName
      ?.toLowerCase()
      .includes(searchTerm?.toLowerCase());
    const matchesCategory =
      filterCategory === "all" || item.category?._id === filterCategory;
    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "available" && item.isAvailable) ||
      (filterStatus === "unavailable" && !item.isAvailable);
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getAvatarColor = (name) => {
    const colors = [
      "#f44336", "#e91e63", "#9c27b0", "#673ab7", "#3f51b5",
      "#2196f3", "#03a9f4", "#00bcd4", "#009688", "#4caf50",
    ];
    const index = name?.charCodeAt(0) % colors.length || 0;
    return colors[index];
  };

  const paginatedItems = filteredItems.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // Stats
  const totalItems = list.length;
  const availableItems = list.filter((item) => item.isAvailable).length;
  const unavailableItems = totalItems - availableItems;
  
  const averagePrice = totalItems > 0
    ? (list.reduce((sum, item) => sum + (item.basePrice || item.price || 0), 0) / totalItems).toFixed(2)
    : 0;

  // Download Excel template
  const downloadTemplate = () => {
    const template = [
      { 
        itemName: "Butter Chicken", 
        basePrice: 350, 
        category: "Main Course",
        description: "Delicious butter chicken",
        isVeg: false,
        sizes: JSON.stringify([{ name: "Half", price: 200 }, { name: "Full", price: 350 }]),
      },
      { 
        itemName: "Garlic Naan", 
        basePrice: 50, 
        category: "Bread",
        description: "Fresh garlic naan",
        isVeg: true,
      },
      { 
        itemName: "Mango Lassi", 
        basePrice: 180, 
        category: "Beverages",
        description: "Fresh mango lassi",
        isVeg: true,
        sizes: JSON.stringify([{ name: "Half", price: 80 }, { name: "Full", price: 180 }]),
      },
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Menu Template");
    XLSX.writeFile(wb, "menu_template.xlsx");
  };

  return (
    <Box sx={{ mb: 4}}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            Menu Management
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body1" color="text.secondary">
              Manage your restaurant menu items with sizes, variants, and addons
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 3, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white", position: "relative", overflow: "hidden" }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>{totalItems}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Items</Typography>
              <RestaurantIcon sx={{ position: "absolute", right: 20, bottom: 20, fontSize: 60, opacity: 0.2 }} />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 3, background: "linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)", color: "white", position: "relative", overflow: "hidden" }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>{availableItems}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Available</Typography>
              <ToggleOnIcon sx={{ position: "absolute", right: 20, bottom: 20, fontSize: 60, opacity: 0.2 }} />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 3, background: "linear-gradient(135deg, #FF9800 0%, #FF5722 100%)", color: "white", position: "relative", overflow: "hidden" }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>{unavailableItems}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Out of Stock</Typography>
              <ToggleOffIcon sx={{ position: "absolute", right: 20, bottom: 20, fontSize: 60, opacity: 0.2 }} />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 3, background: "linear-gradient(135deg, #2196F3 0%, #03A9F4 100%)", color: "white", position: "relative", overflow: "hidden" }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>₹{averagePrice}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Avg. Price</Typography>
              <PriceIcon sx={{ position: "absolute", right: 20, bottom: 20, fontSize: 60, opacity: 0.2 }} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Bar */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "action.active" }} />, sx: { borderRadius: 2 } }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Grid container spacing={1}>
              <Grid size={{ xs: 6}}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select value={filterCategory} label="Category" onChange={(e) => setFilterCategory(e.target.value)} sx={{ borderRadius: 2 }}>
                    <MenuItem value="all">All Categories</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category._id} value={category._id}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar src={category?.image} sx={{ width: 24, height: 24 }}>{category?.name?.charAt(0)}</Avatar>
                          {category?.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)} sx={{ borderRadius: 2 }}>
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="available">Available</MenuItem>
                    <MenuItem value="unavailable">Out of Stock</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton onClick={() => dispatch(fetchMenu())} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download Template">
              <IconButton onClick={downloadTemplate} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setOpenUploadDialog(true)} sx={{ borderRadius: 2 }}>
              Upload Excel
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)} sx={{ borderRadius: 2 }}>
              Add Item
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Table */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "action.hover" }}>
                <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600, width: "25%" }}>Dish Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Base Price</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Sizes/Variants</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedItems?.map((item, index) => (
                <TableRow key={item?._id} hover>
                  <TableCell>#{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar
                        src={item.image || ""}
                        sx={{
                          bgcolor: item.image ? "transparent" : getAvatarColor(item.itemName),
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                        }}
                      >
                        {!item.image && item.itemName?.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item.itemName}
                        </Typography>
                        {item.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {item.description.substring(0, 30)}...
                          </Typography>
                        )}
                        <Chip
                          label={item.isVeg ? "Veg" : "Non-Veg"}
                          size="small"
                          sx={{
                            mt: 0.5,
                            backgroundColor: item.isVeg ? "#e8f5e9" : "#ffebee",
                            color: item.isVeg ? "#2e7d32" : "#c62828",
                            fontSize: "0.7rem",
                            height: 20,
                          }}
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar src={item.category?.image} sx={{ width: 28, height: 28 }}>
                        {item.category?.name?.charAt(0)}
                      </Avatar>
                      {item.category?.name}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: "primary.main" }}>
                      ₹{item.basePrice || item.price}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      {item.sizes?.length > 0 && (
                        <Tooltip title={item.sizes.map(s => `${s.name}: ₹${s.price}`).join(', ')}>
                          <Chip
                            label={`${item.sizes.length} Sizes`}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5, backgroundColor: "#e3f2fd" }}
                          />
                        </Tooltip>
                      )}
                      {item.variants?.length > 0 && (
                        <Tooltip title={item.variants.map(v => `${v.name}`).join(', ')}>
                          <Chip
                            label={`${item.variants.length} Variants`}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5, backgroundColor: "#f3e5f5" }}
                          />
                        </Tooltip>
                      )}
                      {item.addonGroups?.length > 0 && (
                        <Tooltip title={`${item.addonGroups.length} Addon Groups`}>
                          <Chip
                            label={`${item.addonGroups.length} Addons`}
                            size="small"
                            sx={{ backgroundColor: "#fff3e0" }}
                          />
                        </Tooltip>
                      )}
                      {!item.sizes?.length && !item.variants?.length && !item.addonGroups?.length && (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.isAvailable ? "Stock" : "Out of Stock"}
                      color={item.isAvailable ? "success" : "error"}
                      onClick={async () => {
                        try {
                          await dispatch(toggleMenuItem(item?._id)).unwrap();
                          dispatch(fetchMenu());
                        } catch (error) {
                          console.error("Failed to toggle menu item:", error);
                        }
                      }}
                      sx={{ cursor: "pointer", fontWeight: 500, minWidth: 100 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                      <IconButton size="small" onClick={() => handleEdit(item)}>
                        <EditIcon fontSize="small" sx={{ color: "#1976d2" }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteClick(item)}>
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredItems.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* ============ ADD/EDIT DIALOG ============ */}
      <Dialog open={openDialog} onClose={resetForm} maxWidth="md" fullWidth scroll="body">
        <DialogTitle sx={{ borderBottom: "1px solid", borderColor: "divider", pb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ bgcolor: editMode ? "warning.main" : "primary.main" }}>
              {editMode ? <EditIcon /> : <AddIcon />}
            </Avatar>
            <Box>
              <Typography variant="h6">{editMode ? "Edit Menu Item" : "Add New Menu Item"}</Typography>
              <Typography variant="body2" color="text.secondary">
                {editMode ? "Update item details, sizes, variants and addons" : "Create a new menu item with all options"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}>
              Basic Information
            </Typography>
            
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Category *</InputLabel>
                  <Select
                    name="categoryId"
                    value={formData.categoryId}
                    label="Category *"
                    onChange={handleInputChange}
                    required
                  >
                    <MenuItem value=""><em>Select Category</em></MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category._id} value={category._id}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar src={category.image} sx={{ width: 24, height: 24 }}>
                            {category.name?.charAt(0)}
                          </Avatar>
                          {category.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Dish Name *"
                  name="itemName"
                  value={formData.itemName}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  multiline
                  rows={2}
                  placeholder="Enter item description (optional)"
                />
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Base Price *"
                  name="basePrice"
                  type="number"
                  value={formData.basePrice}
                  onChange={handleInputChange}
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                />
              </Grid>
              
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl component="fieldset" margin="normal">
                  <FormLabel component="legend">Food Type</FormLabel>
                  <RadioGroup
                    row
                    name="isVeg"
                    value={formData.isVeg}
                    onChange={(e) => setFormData({ ...formData, isVeg: e.target.value === "true" })}
                  >
                    <FormControlLabel value={true} control={<Radio />} label="Veg" />
                    <FormControlLabel value={false} control={<Radio />} label="Non-Veg" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <Box sx={{ mt: 2, mb: 3 }}>
                  <Button variant="outlined" component="label" startIcon={<ImageIcon />} sx={{ borderRadius: 2 }}>
                    Upload Item Image
                    <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                  </Button>
                  {imagePreview && (
                    <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar src={imagePreview} variant="rounded" sx={{ width: 80, height: 80, borderRadius: 2 }} />
                      <Typography variant="caption" color="text.secondary">
                        Image uploaded successfully
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Sizes Section */}
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Sizes (Optional)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs: 12, md: 5 }}>
                    <TextField
                      fullWidth
                      label="Size Name"
                      size="small"
                      value={currentSize.name}
                      onChange={(e) => setCurrentSize({ ...currentSize, name: e.target.value })}
                      placeholder="e.g., Small, Medium, Large"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Price"
                      size="small"
                      type="number"
                      value={currentSize.price}
                      onChange={(e) => setCurrentSize({ ...currentSize, price: e.target.value })}
                      InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Button
                      variant="contained"
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={handleAddSize}
                      disabled={!currentSize.name || !currentSize.price}
                      fullWidth
                    >
                      Add Size
                    </Button>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                  {formData.sizes.map((size, index) => (
                    <Chip
                      key={index}
                      label={`${size.name}: ₹${size.price}`}
                      onDelete={() => handleRemoveSize(index)}
                      sx={{ m: 0.5 }}
                    />
                  ))}
                  {formData.sizes.length === 0 && (
                    <Typography variant="caption" color="text.secondary">No sizes added</Typography>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Variants Section */}
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Variants (Optional)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs: 12, md: 5 }}>
                    <TextField
                      fullWidth
                      label="Variant Name"
                      size="small"
                      value={currentVariant.name}
                      onChange={(e) => setCurrentVariant({ ...currentVariant, name: e.target.value })}
                      placeholder="e.g., Veg, Non-Veg, Spicy"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Extra Price"
                      size="small"
                      type="number"
                      value={currentVariant.price}
                      onChange={(e) => setCurrentVariant({ ...currentVariant, price: e.target.value })}
                      InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Button
                      variant="contained"
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={handleAddVariant}
                      disabled={!currentVariant.name}
                      fullWidth
                    >
                      Add Variant
                    </Button>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                  {formData.variants.map((variant, index) => (
                    <Chip
                      key={index}
                      label={`${variant.name}${variant.price ? `: +₹${variant.price}` : ""}`}
                      onDelete={() => handleRemoveVariant(index)}
                      sx={{ m: 0.5 }}
                    />
                  ))}
                  {formData.variants.length === 0 && (
                    <Typography variant="caption" color="text.secondary">No variants added</Typography>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Addon Groups Section */}
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Addon Groups (Optional)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>Addon Group Details</Typography>
                  
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        label="Group Title"
                        size="small"
                        value={currentAddonGroup.title}
                        onChange={(e) => setCurrentAddonGroup({ ...currentAddonGroup, title: e.target.value })}
                        placeholder="e.g., Extra Toppings, Sauces"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={currentAddonGroup.required}
                            onChange={(e) => setCurrentAddonGroup({ ...currentAddonGroup, required: e.target.checked })}
                          />
                        }
                        label="Required"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={currentAddonGroup.multiSelect}
                            onChange={(e) => setCurrentAddonGroup({ ...currentAddonGroup, multiSelect: e.target.checked })}
                          />
                        }
                        label="Multi Select"
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>Addon Items</Typography>
                  
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 5 }}>
                      <TextField
                        fullWidth
                        label="Item Name"
                        size="small"
                        value={currentAddonItem.name}
                        onChange={(e) => setCurrentAddonItem({ ...currentAddonItem, name: e.target.value })}
                        placeholder="e.g., Extra Cheese"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        label="Price"
                        size="small"
                        type="number"
                        value={currentAddonItem.price}
                        onChange={(e) => setCurrentAddonItem({ ...currentAddonItem, price: e.target.value })}
                        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleAddAddonItem}
                        disabled={!currentAddonItem.name || !currentAddonItem.price}
                        fullWidth
                      >
                        Add Item
                      </Button>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 2 }}>
                    {currentAddonGroup.items.map((item, index) => (
                      <Chip
                        key={index}
                        label={`${item.name}: ₹${item.price}`}
                        onDelete={() => handleRemoveAddonItem(index)}
                        sx={{ m: 0.5 }}
                      />
                    ))}
                    {currentAddonGroup.items.length === 0 && (
                      <Typography variant="caption" color="text.secondary">No addon items added</Typography>
                    )}
                  </Box>

                  <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      variant="contained"
                      onClick={selectedAddonGroupIndex !== null ? handleUpdateAddonGroup : handleAddAddonGroup}
                      disabled={!currentAddonGroup.title || currentAddonGroup.items.length === 0}
                    >
                      {selectedAddonGroupIndex !== null ? "Update Group" : "Add Group"}
                    </Button>
                  </Box>
                </Paper>

                {/* Added Addon Groups List */}
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Added Addon Groups:</Typography>
                {formData.addonGroups.map((group, index) => (
                  <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Box>
                        <Typography variant="subtitle2">{group.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {group.required ? "Required" : "Optional"} • 
                          {group.multiSelect ? "Multi-select" : "Single-select"} • 
                          {group.items.length} items
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          {group.items.map((item, i) => (
                            <Chip
                              key={i}
                              label={`${item.name}: ₹${item.price}`}
                              size="small"
                              sx={{ m: 0.5 }}
                            />
                          ))}
                        </Box>
                      </Box>
                      <Box>
                        <IconButton size="small" onClick={() => handleEditAddonGroup(index)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleRemoveAddonGroup(index)}>
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                ))}
                {formData.addonGroups.length === 0 && (
                  <Typography variant="caption" color="text.secondary">No addon groups added</Typography>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Availability Switch */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isAvailable}
                  onChange={(e) => setFormData({ ...formData, isAvailable: e.target.checked })}
                />
              }
              label="Item is available for ordering"
              sx={{ mt: 2 }}
            />
          </form>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: "1px solid", borderColor: "divider" }}>
          <Button onClick={resetForm} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.itemName || !formData.basePrice || !formData.categoryId}
            sx={{ borderRadius: 2 }}
          >
            {loading ? "Saving..." : editMode ? "Update Item" : "Create Item"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Excel Dialog */}
      <Dialog open={openUploadDialog} onClose={() => setOpenUploadDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Excel File</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Please upload Excel file with columns: itemName, basePrice, category, description, isVeg, sizes (JSON), variants (JSON)
            </Alert>
            <Button variant="outlined" component="label" fullWidth sx={{ py: 1.5 }}>
              {excelFile ? excelFile.name : "Choose Excel File"}
              <input
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={(e) => setExcelFile(e.target.files[0])}
              />
            </Button>
            {uploading && <LinearProgress sx={{ mt: 2 }} />}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUploadDialog(false)}>Cancel</Button>
          <Button onClick={handleExcelUpload} variant="contained" disabled={!excelFile || uploading}>
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>"{selectedItem?.itemName}"</strong>?
          </Typography>
          {selectedItem?.image && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              <Avatar src={selectedItem.image} variant="rounded" sx={{ width: 80, height: 80 }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={loading}>
            {loading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Messages */}
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => dispatch(clearSuccess())}>
        <Alert severity="success" variant="filled" sx={{ borderRadius: 2 }}>
          {message || "Operation completed successfully!"}
        </Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => dispatch(clearError())}>
        <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
          {error?.message || "Something went wrong!"}
        </Alert>
      </Snackbar>

      {/* Pulse Animation */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(1.2); }
          }
        `}
      </style>
    </Box>
  );
};

export default MenuManagement;